import React, { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import { Button, ThemeSwitcher } from '@/components/ui'
import { cn } from '@/utils/cn'
import { useTheme } from '@/hooks/useTheme'
import { useAuth } from '@/contexts/AuthContext'
import { getLogger } from '@/services/shared/LoggerFactory'
import logoUrl from '@/assets/bitewise-logo.svg'

export interface MobileDrawerProps {
  isOpen: boolean
  onClose: () => void
  className?: string
}

const MobileDrawer: React.FC<MobileDrawerProps> = ({
  isOpen,
  onClose,
  className
}) => {
  const logger = getLogger();
  const { isDark } = useTheme()
  const { isAuthenticated, signOut, isLoading, user } = useAuth()
  const navigate = useNavigate()
  const isPatient = !!user && 'role' in user && user.role === 'patient'
  const drawerRef = useRef<HTMLDivElement>(null)
  const firstFocusableRef = useRef<HTMLButtonElement>(null)

  // Theme-aware styling to match Header component
  const headerBg = isDark ? 'bg-secondary-900/95' : 'bg-secondary-500/95'
  const textColor = 'text-white'
  const buttonHover = isDark ? 'hover:bg-white/15' : 'hover:bg-white/10'

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden' // Prevent background scroll

      // Focus management - focus first focusable element when drawer opens
      setTimeout(() => {
        firstFocusableRef.current?.focus()
      }, 100)
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  // Handle backdrop click
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose()
    }
  }

  // Handle home click with authentication-aware routing
  const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    e.preventDefault()
    if (isAuthenticated) {
      navigate('/dashboard')
    } else {
      navigate('/')
    }
    onClose() // Close the drawer after navigation
  }

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut()
      // Clear navigation history and redirect to home
      window.history.replaceState(null, '', '/')
      window.location.href = '/'
      onClose() // Close the drawer after sign out
    } catch (error) {
      logger.error('Sign out error:', error)
    }
  }

  // Handle support click
  const handleSupportClick = () => {
    window.location.href = 'mailto:support@bitewise.com.au'
    onClose() // Close the drawer after opening email
  }

  const handleShoppingListClick = () => {
    navigate('/shopping-list')
    onClose()
  }

  // Handle about click
  const handleAboutClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    onClose()
    navigate('/about')
  }

  const ShoppingCartIcon = () => (
    <svg
      className="inline-block w-4 h-4 mr-2"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M6 6h15l-1.5 9H8L6 3H3"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="19" r="1.25" fill="currentColor" />
      <circle cx="17" cy="19" r="1.25" fill="currentColor" />
    </svg>
  )

  // Handle sign in click
  const handleSignInClick = () => {
    navigate('/login')
    onClose() // Close the drawer after navigation
  }

  // Handle focus trap
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      const focusableElements = drawerRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )

      if (focusableElements && focusableElements.length > 0) {
        const firstElement = focusableElements[0] as HTMLElement
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault()
            lastElement.focus()
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault()
            firstElement.focus()
          }
        }
      }
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={cn(
          'fixed top-0 left-0 h-full w-80 max-w-[85vw] z-50 lg:hidden',
          'transform transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          isDark
            ? 'bg-secondary-900 border-secondary-700'
            : 'bg-white border-secondary-200',
          'border-r shadow-xl',
          className
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation menu"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="p-4">
          {/* Logo Container with Header-style background */}
          <div className={cn(
            'rounded-full px-6 py-2 backdrop-blur-md border border-white/10',
            'shadow-lg transition-all duration-300 flex items-center justify-between',
            headerBg
          )}>
            {/* BiteWise Logo */}
            <div className="flex items-center">
              <button
                onClick={handleHomeClick}
                className={cn(
                  'rounded-lg p-1 transition-colors duration-200',
                  textColor,
                  buttonHover,
                  'focus:outline-none focus:ring-2 focus:ring-white/20'
                )}
                aria-label="Go to home page"
              >
                <img
                  src={logoUrl}
                  alt="BiteWise"
                  className="h-7 w-auto"
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </button>
            </div>

            {/* Close Button */}
            <Button
              ref={firstFocusableRef}
              variant="ghost"
              size="sm"
              onClick={onClose}
              className={cn(
                'rounded-full p-2',
                textColor,
                buttonHover
              )}
              aria-label="Close menu"
            >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </Button>
          </div>
        </div>

        {/* Navigation Content */}
        <div className="flex flex-col h-full">
          {/* Navigation Links */}
          <nav className="flex-1 px-4 py-6">
            <div className="space-y-4">
              <a
                href="/about"
                className={cn(
                  'block px-4 py-3 rounded-lg font-medium transition-colors',
                  isDark
                    ? 'text-white hover:bg-secondary-800 focus:bg-secondary-800'
                    : 'text-secondary-900 hover:bg-secondary-100 focus:bg-secondary-100',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500'
                )}
                onClick={handleAboutClick}
                title="About Bitewise"
                aria-label="About Bitewise"
              >
                About
              </a>

              <button
                onClick={handleSupportClick}
                className={cn(
                  'block px-4 py-3 rounded-lg font-medium transition-colors text-left w-full',
                  isDark
                    ? 'text-white hover:bg-secondary-800 focus:bg-secondary-800'
                    : 'text-secondary-900 hover:bg-secondary-100 focus:bg-secondary-100',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500'
                )}
                aria-label="Contact Support"
                title="Contact Support"
              >
                Support
              </button>

              {isPatient && (
                <button
                  onClick={handleShoppingListClick}
                  className={cn(
                    'block px-4 py-3 rounded-lg font-medium transition-colors text-left w-full',
                    isDark
                      ? 'text-white hover:bg-secondary-800 focus:bg-secondary-800'
                      : 'text-secondary-900 hover:bg-secondary-100 focus:bg-secondary-100',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500'
                  )}
                  aria-label="Shopping List"
                  title="Shopping List"
                >
                  <ShoppingCartIcon />
                  Shopping List
                </button>
              )}

              {/* Authentication Section in Navigation */}
              <div className={cn('border-t pt-4 mt-4', isDark ? 'border-secondary-700' : 'border-secondary-200')}>
                {isAuthenticated ? (
                  <button
                    onClick={handleSignOut}
                    disabled={isLoading}
                    className={cn(
                      'block px-4 py-3 rounded-lg font-medium transition-colors text-left w-full',
                      isDark
                        ? 'text-white hover:bg-secondary-800 focus:bg-secondary-800'
                        : 'text-secondary-900 hover:bg-secondary-100 focus:bg-secondary-100',
                      'focus:outline-none focus:ring-2 focus:ring-primary-500'
                    )}
                    aria-label="Sign Out"
                  >
                    <svg className="inline-block w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    {isLoading ? 'Signing out...' : 'Sign Out'}
                  </button>
                ) : (
                  <button
                    onClick={handleSignInClick}
                    className={cn(
                      'block px-4 py-3 rounded-lg font-medium transition-colors text-left w-full',
                      isDark
                        ? 'text-white hover:bg-secondary-800 focus:bg-secondary-800'
                        : 'text-secondary-900 hover:bg-secondary-100 focus:bg-secondary-100',
                      'focus:outline-none focus:ring-2 focus:ring-primary-500'
                    )}
                    aria-label="Sign In"
                  >
                    <svg className="inline-block w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                    Login
                  </button>
                )}
              </div>
            </div>
          </nav>

          {/* Footer Section */}
          <div className={cn(
            'px-4 py-6 border-t',
            isDark ? 'border-secondary-700' : 'border-secondary-200'
          )}>
            {/* Theme Switcher */}
            <div className="flex items-center justify-between">
              <span className={cn(
                'text-sm font-medium',
                isDark ? 'text-secondary-300' : 'text-secondary-600'
              )}>
                Theme
              </span>
              <ThemeSwitcher
                variant="mobile"
                size="sm"
                showLabel={false}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export { MobileDrawer }